/**
 * The chart specification — design/09 §7 and G2 §2.5, as a type.
 *
 * THE DESIGN RULE OF THIS FILE: a violating chart is not constructible. Not
 * "discouraged", not "caught in review" — the type does not describe it.
 *
 * G2 §2.5: "These are implemented in a charting wrapper over Apache ECharts; the
 * underlying library API is not exposed to feature code, so a violating chart is
 * not constructible." There is deliberately no `echartsOption`, no `raw`, no
 * `override` and no index signature anywhere in this file. If a chart cannot be
 * expressed here, the answer is a new sanctioned form with its own review — not an
 * escape hatch.
 *
 * WHAT IS ABSENT, AND WHY (G2 test c, design/09 §12.4):
 *
 *   smooth         interpolation invents values that were never observed and
 *                  displaces the visual timing of peaks and troughs. Time series
 *                  are straight segments between observations. Always
 *   secondaryAxis  there is ONE `valueAxis` field and it is not an array. Two
 *                  measures of different scale become two charts, small multiples,
 *                  or both indexed to a common base
 *   3d             perspective distorts area and length comparison
 *   gauge          angle and arc are the worst encodings for comparison; a single
 *                  KPI is a StatTileSpec, not a dial
 *   donut / pie    same, plus §7.5 is explicit: "Not a chart. Not a donut"
 *
 * `banned-options.typetest.ts` asserts each of these is absent, and fails if one is
 * ever added — see tsconfig.typetests.json for how the assertion inverts.
 */

import type { MissingState } from '../export/types';
import type { ICUValues } from '../primitives/display';

export type { ICUValues };

// ── the closed set of sanctioned forms ───────────────────────────────────────

/**
 * Every chart form the product can render. Adding to this list is a design
 * decision requiring an ADR; there is no generic escape.
 *
 * Note what is not here: pie, donut, gauge, radar, funnel, treemap, word cloud,
 * packed bubble, radial bar, and anything ending in 3D.
 */
export const CHART_FORMS = [
  'line', // time series — straight segments, always
  'step-line', // §7.5 — a discrete ladder held between observations; see StepLine below
  'bar', // single series over a category axis, from zero
  'clustered-bar', // ≤5 categories on X (§2.5 element caps)
  'stacked-bar', // ≤7 categories on X
  'paired-bar', // §7.5 — size curve, planned vs actual vs sold
  'diverging-bar', // §7.5 — sell-through vs plan, blue↔red, neutral at zero
  'area', // cumulative magnitude, from zero
  'scatter', // ≤3 series before faceting (§7.2)
  'heatmap', // §7.5 — size-run completeness, store × size, sequential blue
  'histogram', // §7.5 — weeks-of-cover distribution, with a target band
  'small-multiples', // §7.5 — cluster comparison, one panel per cluster
] as const;

export type ChartForm = (typeof CHART_FORMS)[number];

/** Named only so the type tests and the lint can share one list. */
export const BANNED_OPTION_NAMES = [
  'smooth', 'secondaryAxis', 'secondaryYAxis', 'yAxisIndex', '3d', 'grid3D',
  'gauge', 'donut', 'pie', 'radar', 'funnel', 'wordCloud', 'globe',
] as const;
export type BannedOptionName = (typeof BANNED_OPTION_NAMES)[number];

// ── values, and the three states of a missing one ────────────────────────────

/**
 * reqs/00 D8 and G2 §2.5: "A gap in a series is a gap, not a joined line and not a
 * zero … a store that was not ranged is not a store that sold nothing, and no chart
 * may imply otherwise."
 *
 * So a datum is a number OR an explicit missing state. `null` is not accepted and
 * neither is `0`-as-absence: the caller must say which of the three it means, and
 * the renderer draws each differently.
 */
export interface MissingDatum {
  readonly missing: Exclude<MissingState, 'counted-zero'>;
}

export type Datum = number | MissingDatum;

/**
 * The guard's predicate names `MissingDatum` exactly rather than restating its shape.
 * That matters: a structurally identical but separately declared type does not let
 * TypeScript narrow the else-branch to `number`, and every arithmetic site downstream
 * would then need a cast — which is how a missing value ends up silently coerced to zero.
 */
export const isMissing = (d: Datum): d is MissingDatum =>
  typeof d === 'object' && d !== null && 'missing' in d;

// ── axes ─────────────────────────────────────────────────────────────────────

/**
 * The value axis. Exactly one, never an array — this is where §12.4 "no chart in
 * the product has two y-axes" is enforced structurally.
 */
export interface ValueAxis {
  /**
   * Unit of measure. REQUIRED. G2 §2.5: "Axis legend and unit of measure on both
   * axes where meaningful", and "Maximum three digits on value-axis labels — use
   * k / m and say so in the unit". The wrapper scales the labels and appends the
   * magnitude to this unit; the caller states the base unit.
   */
  readonly unitKey: string;
  /**
   * `false` is the only value a bar form accepts. G2 §2.5: "Truncated value axes on
   * bar charts" are banned — a bar encodes length from zero. Line charts may zoom.
   */
  readonly zeroBased: boolean;
  readonly minKey?: never;
  /**
   * The axis's extent, stated, for two charts read side by side on ONE scale — layout R's 6 + 6
   * band, where the same option's price path under two objectives is compared by eye (issue 226,
   * ADR 9038's disposition R). Without it each chart fits its own data, and two different heights
   * read as the same price. The caller passes the SAME range to both; every datum must lie inside
   * it (`validate.ts` throws rather than clip a point off the plot), and a zero-based axis must
   * start at 0, so a range can never truncate a bar.
   */
  readonly sharedRange?: { readonly min: number; readonly max: number };
  /** Optional target band annotation — §7.5's weeks-of-cover histogram. */
  readonly targetBand?: { readonly from: number; readonly to: number; readonly labelKey: string };
}

/**
 * A category axis. `kind` decides ordering, and this is the only place ordering is
 * decided — the renderer never sorts.
 */
export type CategoryAxis =
  | {
    /**
     * design/09 §12.11 and §7.5: "Size axes render in scale order, never
     * lexicographic. `EU 40` before `EU 41`; never `10, 11, 9`."
     *
     * The order is DECLARED, not inferred, because inferring it is exactly how
     * `10, 11, 9` happens. `assertChartSpec` fails if a series carries a category
     * absent from this list.
     */
    readonly kind: 'size-scale';
    readonly labelKey: string;
    readonly order: readonly string[];
  }
  | {
    /** Ordered by meaning: lifecycle stage, price tier, week. Order declared. */
    readonly kind: 'ordinal';
    readonly labelKey: string;
    readonly order: readonly string[];
  }
  | {
    /** Unordered nominal categories — stores, clusters. Rendered in given order. */
    readonly kind: 'nominal';
    readonly labelKey: string;
  }
  | {
    /** Time. Straight segments between observations; there is no smoothing option. */
    readonly kind: 'time';
    readonly labelKey: string;
  };

/**
 * A NUMERIC x axis — `line`, `area` and `scatter` only (ADR 9013's chart extension).
 *
 * A category axis spaces its points evenly, which is right for weeks and sizes and wrong for
 * a measure: a Pareto curve published as sparse points dense at the head (rank 1, 2, 3 … 40,
 * 120, 7 976) drawn on categories stretches the head across half the chart, and a scatter of
 * one measure against another has no categories at all. Here each point sits at its TRUE x.
 *
 * `values` is the x of every point, aligned by index with every series' `data` — the same
 * contract as a category axis's `order`, so the table view and the CSV still read one row per
 * point, and the x leaves the file as the NUMBER it is, never as a label. For `line` and `area`
 * the values must strictly increase (a line is drawn in x order and nothing here sorts); a
 * `scatter` may repeat an x. A point with no y is a declared missing datum, as everywhere.
 *
 * It is still ONE x axis and one value axis — §12.4's single value axis is untouched, and a
 * numeric x is not a second y. Ticks are formatted by the wrapper's locale-aware formatter,
 * scaled to three digits with the magnitude in the axis name, exactly like the value axis.
 */
export interface NumericAxis {
  readonly kind: 'numeric';
  readonly labelKey: string;
  /** The x axis's unit — G2 §2.5 "unit of measure on both axes". Required. */
  readonly unitKey: string;
  /** The x of each point, aligned with every series' `data`. */
  readonly values: readonly number[];
  /** `true` draws the axis from zero; `false` fits it to the values. */
  readonly zeroBased: boolean;
  /**
   * `log` spaces the axis by ratio, base 2 — one tick per doubling (ADR 9050). Only on a
   * `scatter` or a `line`, only with every x above zero, and never `zeroBased`: a log axis has
   * no zero. For a measure whose doors range from 5 to 80 weeks, where the question is what a
   * DOUBLING buys; a linear axis would crush the head into the first tick. Absent: `linear`.
   */
  readonly scale?: 'linear' | 'log';
}

/**
 * A reference line on a chart with a numeric x axis — the diagonal a shadow-versus-incumbent
 * scatter is read against, the 50% / 80% thresholds of a Pareto curve.
 *
 * The surface stays two or three facts, like `EventLine`: WHERE, and what it is CALLED. There
 * is no colour, width, dash or offset — every reference is drawn the same recessive way in both
 * themes (design/09 §7.4), and a label is a catalogue key, with values placed by the translator.
 *
 *   `identity`  the line y = x. Only where the x axis and the value axis are in the SAME unit —
 *               a diagonal between two different units asserts an equivalence nobody measured,
 *               so `assertChartSpec` refuses it
 *   `x`         a vertical marker at `at` on the numeric x axis, inside the axis's values
 *   `y`         a horizontal marker at `at` on the value axis, inside the plotted values
 *
 * §12 criterion 14 holds: a position outside what the axis carries fails the spec rather than
 * being drawn at the edge.
 */
export type ReferenceMark =
  | { readonly kind: 'identity'; readonly labelKey: string; readonly labelValues?: ICUValues }
  | { readonly kind: 'x'; readonly at: number; readonly labelKey: string; readonly labelValues?: ICUValues }
  | { readonly kind: 'y'; readonly at: number; readonly labelKey: string; readonly labelValues?: ICUValues }
  /**
   * A trend the caller computed from the points — a median by band (ADR 9050). Straight
   * segments between the stated points, never a fitted curve and never smoothed: each vertex
   * is a summary the caller can name, and the table view lists the vertices. Only on a
   * `scatter`; x strictly increasing and inside the axis's values, y inside the plotted values.
   * Drawn in the title ink, solid, under the dots — a reading aid, not a series: it has no
   * colour slot, no legend entry and no tooltip.
   */
  | {
    readonly kind: 'path';
    readonly points: readonly { readonly x: number; readonly y: number }[];
    readonly labelKey: string;
    readonly labelValues?: ICUValues;
  };

/** The forms that accept a numeric x axis and reference marks. */
export const NUMERIC_X_FORMS = ['line', 'area', 'scatter'] as const;

/** What the three numeric-capable forms add to `ChartBase`. */
interface NumericCapable {
  /** A category axis, as every form — or a numeric one (`NumericAxis`). */
  readonly categoryAxis: CategoryAxis | NumericAxis;
  /** Reference lines; only with a numeric x axis, at most three. */
  readonly references?: readonly ReferenceMark[];
}

// ── annotations on the category axis ─────────────────────────────────────────

/**
 * A named marker on the CATEGORY axis — a floor-set week, a drop date, a markdown
 * step, a season boundary, a launch week.
 *
 * `valueAxis.targetBand` is the value-axis equivalent and is the wrong axis for a
 * dated deadline: a handover week is a position along the categories, not a range of
 * the measure. Before this existed a chapter could only state the date in its
 * perimeter subtitle, which leaves the reader to count columns.
 *
 * THE SURFACE IS TWO STRINGS AND IT STAYS TWO STRINGS. This is exactly the field a
 * wrapper starts leaking through — an annotation API is where `style`, `lineStyle`,
 * `symbol`, `z` and eventually `option` arrive one prop at a time. There is
 * deliberately no colour, no width, no dash pattern, no position, no offset and no
 * ECharts-shaped anything here: the caller says WHERE and WHAT IT IS CALLED, and the
 * wrapper decides how a recessive annotation looks (design/09 §7.4's hairline
 * treatment) identically in both themes. A chapter that wants a differently-styled
 * marker is asking for a design decision, not a prop.
 */
export interface EventLine {
  /**
   * The category the marker sits at, verbatim from the axis's declared `order`.
   *
   * `assertChartSpec` fails if the axis does not carry it. An event line at an unknown
   * category is a defect — ECharts would silently place it at the origin, and a
   * handover marker drawn on the wrong week is worse than no marker at all.
   */
  readonly at: string;
  /** ICU key for the marker's name. G6 — never a literal, in the chart or the table. */
  readonly labelKey: string;
}

// ── series ───────────────────────────────────────────────────────────────────

/**
 * G2 §2.5: "Series distinguishable by more than hue — pattern, marker or direct
 * label, so the chart survives greyscale printing and CVD."
 *
 * Required on every series. `assertChartSpec` additionally requires that a
 * multi-series chart does not give two series the same distinguisher.
 */
export const DISTINGUISHERS = ['marker-circle', 'marker-square', 'marker-triangle', 'marker-diamond', 'pattern-solid', 'pattern-hatch', 'pattern-dot', 'direct-label'] as const;
export type Distinguisher = (typeof DISTINGUISHERS)[number];

export interface Series {
  /** Stable entity id. G2 §2.5: "Colour follows the entity, never its rank" —
   *  filtering a store list must not repaint the survivors, so the palette slot is
   *  resolved from this id and never from array position. */
  readonly id: string;
  /** ICU key for the series name. G6 — never a literal. */
  readonly nameKey: string;
  /**
   * ICU arguments for `nameKey`, for a series named after an ENTITY — a door, a store, a
   * supplier — rather than after a concept.
   *
   * Without it the only way to name five doors was five catalogue keys, which works for an
   * illustrative fixture and cannot work for real data: the estate has hundreds of doors and
   * the catalogue is prose, not a store register. Audit v4 G-03 and its twin on the launch
   * size-run screen were both "every row is called Door" for exactly that reason. The key
   * stays the translated sentence (`"{door}"`, or `"{door} · outlet"`); the value is the
   * business name, passed as an argument the translator placed — the same rule that renders a
   * season code as the key it is rather than through `t()`.
   */
  readonly nameValues?: ICUValues;
  readonly data: readonly Datum[];
  readonly distinguisher: Distinguisher;
  /**
   * The x of each point, when the series is measured at its OWN positions on a numeric x axis
   * (ADR 9050) — an exchange curve's rules are evaluated at different stock levels, so their
   * points cannot share one list of x. Aligned with `data`; strictly increasing on a line;
   * every value must be one of the axis's `values`, which then list every position any series
   * is drawn at. Without it the series is aligned to the axis's `values`, as always. The table
   * view and the export read such a chart one row per POINT (series, x, value).
   */
  readonly at?: readonly number[];
  /**
   * What each point IS, as a translated sentence with that point's values (ADR 9050): a door
   * («{door} · {format}»), a stocking level («p{level}»). `values` is aligned with `data`. The
   * tooltip reads it as `{point}`, the table view carries it as a column, and with `drawn` the
   * label is written beside the mark — a direct label, for a series of a handful of points.
   */
  readonly pointLabels?: {
    readonly key: string;
    readonly values: readonly ICUValues[];
    readonly drawn?: boolean;
  };
  /**
   * Points drawn HOLLOW — an outline, no fill — aligned with `data` (ADR 9050). A point that is
   * derived rather than read (a level computed from a fitted distribution beside levels the
   * forecast stored) is told apart by its fill, never by colour alone: the shape and the
   * series stay, and the caller says in the perimeter what hollow means.
   */
  readonly hollow?: readonly boolean[];
  /**
   * Explicit palette slot, 1–7. Omit to have the wrapper assign one by hashing
   * `id`, which is stable under filtering. A status colour is not assignable here —
   * §7.4: "Status colours never double as series colours."
   */
  readonly slot?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
}

// ── the base every chart shares ──────────────────────────────────────────────

export interface ChartBase {
  /**
   * G2 §2.5: "Action title, maximum one line — the finding, not the subject
   * ('Size 41 is out of stock in 38% of CO doors', not 'Stock by size')."
   *
   * An ICU key. `assertChartSpec` checks the RESOLVED string for a newline and for
   * length, because the one-line rule is about the rendered result and a 40%
   * pseudo-localisation expansion (G6) is exactly when it breaks.
   */
  readonly actionTitleKey: string;
  /**
   * ICU arguments for `actionTitleKey` — `docs/adr/0093`.
   *
   * THE PROBLEM THIS SOLVES, and why it is not a hole in G6. A chart that titles itself with
   * a value from the service — a cluster id, a door code, an option key — has no catalogue key
   * for that value, because the value is reference data the platform coined and this build has
   * never seen. G6 forbids the two obvious escapes: a user-visible string literal, and building
   * the sentence with `+` or a template literal. Without a third road a chapter cannot title a
   * per-entity panel at all, which is what stopped ch05 rendering cluster small multiples.
   *
   * The road is the one the rest of the product already uses (`grid.expand`, `grid.editCell`,
   * `Measure`, `Toast`): the SENTENCE stays in the catalogue and is translated; the DATUM is
   * interpolated into it. An `actionTitleKey` in the chapter's own namespace resolving
   * *"Cluster {cluster}"*, with `actionTitleValues` carrying the id, is a translated sentence
   * carrying a value — not a literal — and it reorders correctly in Italian and Spanish,
   * which concatenation does not.
   *
   * (No example key is spelled out here on purpose: `i18n:coverage` does not strip comments,
   * so a key-shaped string in a doc block is a key the build believes the code references.)
   *
   * `assertOneLineTitle` runs on the RESOLVED string, so a long interpolated value still fails
   * the one-line rule. That is deliberate: the check is about what is rendered.
   */
  readonly actionTitleValues?: ICUValues;
  /**
   * G2 §2.5: "Subtitle stating the exact perimeter — time window, geography,
   * segment." Required. The evo rule "state the perimeter of analysis" is enforced
   * by the type, not by discipline.
   */
  readonly perimeterKey: string;
  /**
   * ICU arguments for `perimeterKey` — a perimeter that states a live datum (the weeks a window
   * actually landed, ADR 9050's amendment), placed by the translator, never concatenated.
   */
  readonly perimeterValues?: ICUValues;
  readonly valueAxis: ValueAxis;
  readonly categoryAxis: CategoryAxis;
  readonly series: readonly Series[];
  /**
   * Declared markers on the category axis — see `EventLine`. Optional, capped, and
   * permitted only on an axis that declares its order, because a marker that cannot
   * be checked against a category list is a marker that can silently land anywhere.
   */
  readonly eventLines?: readonly EventLine[];
  /**
   * design/09 §12.10 and G2 §2.5: "A reachable table view, always." There is no
   * field to switch it off — `<Chart>` always renders the affordance. This flag
   * exists only to record the accessible name of the toggle.
   */
  readonly tableViewLabelKey?: string;
  /** ICU key for the chart's accessible description (§12.7, §12.8). */
  readonly descriptionKey?: string;
  /**
   * The sentence a mark's tooltip reads — ICU, with `{category}`, `{value}` (already
   * formatted for the locale) and `{series}` as arguments. "2026-W44: 8 price steps" says
   * what the mark IS; ECharts' default ("Actual · EU 41 · 19") only says where it sits.
   * Absent, the default stays. Per-mark tooltips only (bar, dot, cell) — a line's crosshair
   * tooltip lists every series and keeps its own layout.
   */
  readonly tooltipKey?: string;
}

// ── the forms ────────────────────────────────────────────────────────────────

/**
 * `line` and `step-line`.
 *
 * `step-line` is NOT a styling variant and it is not the inverse of the banned
 * `smooth`. It states a different fact about the measure between two observations.
 *
 *   line       the measure moved continuously and we observed it at these points.
 *              Straight segments, because interpolation is the honest reading of an
 *              unobserved continuous quantity
 *   step-line  the measure DID NOT MOVE between the observations — it was held, and
 *              then it changed. A price ladder is the case §7.5 names: *"A price
 *              ladder is discrete and a smoothed line across a step asserts prices
 *              that were never charged."* A straight segment between rung 3 and
 *              rung 5 draws a week at rung 4 that no shopper was ever offered
 *
 * So drawing a ladder as `line` is the same class of error as `smooth`, one degree
 * milder, and the two forms exist so that a caller has to say which it means.
 */
export interface LineChartSpec extends Omit<ChartBase, 'categoryAxis'>, NumericCapable {
  readonly form: 'line' | 'step-line';
  /** Line charts may zoom their value axis; bars may not. */
  readonly showMarkers?: boolean;
}

export interface AreaChartSpec extends Omit<ChartBase, 'categoryAxis'>, NumericCapable {
  readonly form: 'area';
}

export interface BarChartSpec extends ChartBase {
  readonly form: 'bar' | 'clustered-bar' | 'stacked-bar' | 'paired-bar';
  readonly orientation?: 'vertical' | 'horizontal';
}

export interface DivergingBarChartSpec extends ChartBase {
  readonly form: 'diverging-bar';
  /** The value at which the neutral midpoint sits — zero variance (§7.3). */
  readonly neutralAt: number;
  /** Bars along the value axis (`vertical`, the default) or across it, one per row. */
  readonly orientation?: 'vertical' | 'horizontal';
}

export interface ScatterChartSpec extends Omit<ChartBase, 'categoryAxis'>, NumericCapable {
  readonly form: 'scatter';
  /** Bubble size measure. §7.2 caps scatter and bubble at three series. */
  readonly sizeUnitKey?: string;
  /**
   * An interval at each category (ADR 9051): the ids of the two series that hold its LOWER and
   * UPPER bound, drawn joined by a whisker through the third series' point. The bounds stay
   * series: the table view and the file carry them as columns in the value axis's unit, so the
   * interval is never a picture only. Only on a category axis with a declared order; at every
   * category where both bounds are present the lower is not above the upper.
   */
  readonly interval?: { readonly low: string; readonly high: string };
}

export interface HeatmapChartSpec extends ChartBase {
  readonly form: 'heatmap';
  /** The second (row) axis — store, door, cluster. */
  readonly rowAxis: CategoryAxis;
  /** §7.3 — single blue hue for magnitude; orange for a second simultaneous ramp. */
  readonly ramp: 'blue' | 'orange';
}

export interface HistogramChartSpec extends ChartBase {
  readonly form: 'histogram';
}

export interface SmallMultiplesChartSpec extends ChartBase {
  readonly form: 'small-multiples';
  /** One panel per entry. §7.5: "Never seven overlaid lines." */
  readonly panels: readonly { readonly id: string; readonly titleKey: string; readonly series: readonly Series[] }[];
  readonly panelForm: 'line' | 'bar';
}

export type ChartSpec =
  | LineChartSpec
  | AreaChartSpec
  | BarChartSpec
  | DivergingBarChartSpec
  | ScatterChartSpec
  | HeatmapChartSpec
  | HistogramChartSpec
  | SmallMultiplesChartSpec;

// ── the stat tile, which is deliberately not a chart ─────────────────────────

/**
 * design/09 §7.5: "Single KPI (TTM sales, terminal stock %) — Stat tile,
 * `type-metric`. Not a chart. Not a donut."
 *
 * It is a separate type from `ChartSpec` so that a single KPI cannot be routed
 * through the charting path and come out as a dial.
 */
export interface StatTileSpec {
  readonly labelKey: string;
  readonly value: Datum;
  readonly unitKey: string;
  /**
   * design/09 §5.3: "Direction of 'good' is configurable per measure and must be.
   * Sell-through up is good; markdown up is not; stock cover up is neither without a
   * target. Never hard-code green-means-up."
   */
  readonly polarity: 'higher-is-better' | 'lower-is-better' | 'target-relative' | 'none';
  /**
   * Show exactly this many decimals. For tiles set side by side, so their figures share a
   * precision and line up: omit it and each tile picks its own, which is how three ratios
   * rendered as 0.32, 0.4 and 0.09.
   */
  readonly fractionDigits?: number;
  readonly comparison?: {
    readonly value: number;
    readonly labelKey: string;
  };
  /** A sparkline is permitted; a donut is not, and there is no field for one. */
  readonly trend?: readonly Datum[];
  /**
   * The list behind the figure (`docs/adr/9069`): the whole tile becomes a link to it, built with
   * the screen's own route helpers; the list must sum to this figure. Absent: the tile is what it was.
   */
  readonly href?: string;
}
